import * as THREE from 'three';
import { TrackSupportPrimitive } from '../core/supports/types';
import { SceneHost } from './scene';

/** Vite HMR: dispose the previous instance before remounting. */
let disposePrevious: (() => void) | null = null;

type StubHandle = {
  attachS: number;
  a: THREE.Vector3;
  b: THREE.Vector3;
  radiusA: number;
  radiusB: number;
};

/**
 * Pickable attach stubs so a support station can be selected in the viewport
 * (Delete → suppress override). Steel stays merged in SupportMesh.
 */
export class SupportStubEditor {
  readonly group = new THREE.Group();
  private readonly handles = new THREE.Group();
  private readonly raycaster = new THREE.Raycaster();
  private readonly ndc = new THREE.Vector2();
  private readonly pickMat = new THREE.MeshBasicMaterial({
    transparent: true,
    opacity: 0.01,
    depthWrite: false,
  });
  private readonly selectMat = new THREE.MeshBasicMaterial({
    color: 0xe8a838,
    transparent: true,
    opacity: 0.9,
    depthWrite: false,
    depthTest: false,
  });
  private stubs: StubHandle[] = [];
  private selectedAttachS: number | null = null;
  private enabled = true;

  onSelect: ((attachS: number | null) => void) | null = null;

  constructor(_host: SceneHost) {
    disposePrevious?.();

    this.group.name = 'supportStubEditor';
    this.handles.name = 'supportStubHandles';
    this.group.add(this.handles);

    disposePrevious = () => {
      this.clearHandles();
      this.pickMat.dispose();
      this.selectMat.dispose();
    };
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    this.group.visible = on;
    if (!on) this.clearSelection();
  }

  get selection(): number | null {
    return this.selectedAttachS;
  }

  /** Rebuild invisible pick tubes from the latest solver stubs. */
  syncStubs(primitives: TrackSupportPrimitive[]): void {
    this.stubs = [];
    for (const p of primitives) {
      if (p.kind !== 'stub' || p.attachS == null) continue;
      if (p.a.distanceToSquared(p.b) < 1e-8) continue;
      this.stubs.push({
        attachS: p.attachS,
        a: p.a.clone(),
        b: p.b.clone(),
        radiusA: p.radiusA,
        radiusB: p.radiusB,
      });
    }
    const prev = this.selectedAttachS;
    if (
      this.selectedAttachS != null &&
      !this.stubs.some((s) => Math.abs(s.attachS - this.selectedAttachS!) < 1e-4)
    ) {
      this.selectedAttachS = null;
    }
    this.rebuildHandles();
    if (prev !== this.selectedAttachS) {
      this.onSelect?.(this.selectedAttachS);
    }
  }

  clearSelection(): void {
    if (this.selectedAttachS == null) return;
    this.selectedAttachS = null;
    this.rebuildHandles();
    this.onSelect?.(null);
  }

  setSelection(attachS: number | null): void {
    if (attachS == null) {
      this.clearSelection();
      return;
    }
    const stub = this.stubs.find((s) => Math.abs(s.attachS - attachS) < 1e-4);
    if (!stub) {
      this.clearSelection();
      return;
    }
    if (this.selectedAttachS != null && Math.abs(this.selectedAttachS - stub.attachS) < 1e-4) {
      return;
    }
    this.selectedAttachS = stub.attachS;
    this.rebuildHandles();
    this.onSelect?.(this.selectedAttachS);
  }

  /** Ray-pick an attach stub under the cursor. */
  pickAtScreen(
    camera: THREE.Camera,
    clientX: number,
    clientY: number,
    canvas: HTMLElement,
  ): number | null {
    if (!this.enabled || !this.group.visible) return null;
    this.setPointer(camera, clientX, clientY, canvas);
    const hits = this.raycaster.intersectObjects(this.handles.children, false);
    for (const hit of hits) {
      const ud = hit.object.userData as { attachS?: number };
      if (ud.attachS == null) continue;
      return ud.attachS;
    }
    return null;
  }

  private setPointer(
    camera: THREE.Camera,
    clientX: number,
    clientY: number,
    canvas: HTMLElement,
  ): void {
    const rect = canvas.getBoundingClientRect();
    this.ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.ndc, camera);
  }

  private rebuildHandles(): void {
    this.clearHandles();
    for (const stub of this.stubs) {
      const selected =
        this.selectedAttachS != null && Math.abs(this.selectedAttachS - stub.attachS) < 1e-4;
      const dir = new THREE.Vector3().subVectors(stub.b, stub.a);
      const len = Math.max(dir.length(), 1e-6);
      const pad = selected ? 0.12 : 0.08;
      const geo = new THREE.CylinderGeometry(
        stub.radiusB + pad,
        stub.radiusA + pad,
        len,
        10,
      );
      geo.translate(0, len / 2, 0);
      const quat = new THREE.Quaternion().setFromUnitVectors(
        new THREE.Vector3(0, 1, 0),
        dir.normalize(),
      );
      geo.applyQuaternion(quat);
      geo.translate(stub.a.x, stub.a.y, stub.a.z);

      const mesh = new THREE.Mesh(geo, selected ? this.selectMat : this.pickMat);
      mesh.userData = { attachS: stub.attachS };
      mesh.renderOrder = selected ? 8 : 2;
      this.handles.add(mesh);
    }
  }

  private clearHandles(): void {
    for (const child of [...this.handles.children]) {
      this.handles.remove(child);
      if (child instanceof THREE.Mesh) child.geometry.dispose();
    }
  }
}
